import { Component, ElementRef, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { ASCIIFolder, Product, Stock } from '../../models/entities';
import { SharedService } from '../../services/shared.service';
import { environment } from 'src/environments/environment';
import { SubSink } from 'subsink';
import { Store, select } from '@ngrx/store';
import { State, selectCurrentDynaData } from 'src/app/store/shared/shared.reducers';
import { Observable, debounceTime, distinctUntilChanged, filter, fromEvent, map, switchMap } from 'rxjs';
import { DynaCrudService, DynacrudApiWrapper } from 'src/app/core/api/dynacrud-api';
import { DynacrudApi, OperationType, SearchRequest } from 'src/app/core/models/dynacrud';
import { AppLocaleService } from '../../services/app-locale.service';
import { localizedName } from '../../i18n/localized-name';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';

@Component({
  selector: 'app-default-search-stock-product',
  templateUrl: './default-search-stock-product.component.html',
  styleUrls: ['./default-search-stock-product.component.scss']
})
export class DefaultSearchStockProductComponent implements OnInit {
  valueProducts: {[k: string] : Product | undefined} = {};
  loading = false;
  @ViewChild('searchStock') searchStock!: ElementRef;
  @ViewChild(MatAutocompleteTrigger) autoTrigger?: MatAutocompleteTrigger;
  productResult: Product[] = [];
  /**
   * Enlarged-image preview state. ONE component-owned overlay, not one per `mat-option`: an
   * option's view dies with the click that picks the product, and WebKit fires no
   * `mouseout`/`mouseleave` for a node removed under the pointer, so a per-option overlay stayed
   * on screen with nothing able to dismiss it. See hidePreview().
   */
  previewProduct: Product | null = null;
  previewOrigin: CdkOverlayOrigin | null = null;
  showImg = false;
  private subs = new SubSink();
  
  stockService: DynaCrudService<Stock>;

  constructor(private sharedService: SharedService, private store: Store<State>, private dynacrudApiWrapper: DynacrudApiWrapper, private localeService: AppLocaleService) {
    this.stockService = this.dynacrudApiWrapper.getFor('product');
  }

  /** True once a product is picked for the active code (input becomes readonly, not disabled). */
  get isProductPicked(): boolean {
    return !!this.valueProducts[this.code]?.id;
  }

  /**
   * Localized display text for the picked product, bound to the input's [value] so the
   * name sits in a real (readonly) input the user can select/copy. Read each change-detection
   * pass, so it re-localizes on language switch (matches the mat-label format).
   */
  get pickedProductName(): string {
    const p = this.valueProducts[this.code];
    if (!p) {
      return '';
    }
    const base = localizedName(p, this.localeService.current) ?? '';
    return p.expansion ? `${base} (${p.expansion.enName ?? ''})` : base;
  }

  @Input() isVisible?: boolean;

  code: string = '';
  
  ngOnInit(): void {
    this.subs.add(this.store.pipe(select(selectCurrentDynaData), distinctUntilChanged(), debounceTime(0)).subscribe(request => {

      if (request.route && request.route.code) {
        this.code = request.route.code;

        this.valueProducts[this.code] = request.request?.customData?.product || undefined;
      }

    }));
  }

  // add hostlistener
  
  @HostListener('document:keyup.esc')
  onDocumentClick() {
    if ((this.valueProducts && this.valueProducts[this.code]?.id) || (this.valueProducts && this.valueProducts[this.code]?.idMetaProduct) ) {
      this.clearSelect();
      this.reopenCachedResults();
    }
  }

  /**
   * Esc-to-reopen: after clearSelect() un-picks the product, re-open the autocomplete over the
   * cached previous-search results (`productResult`) so the user can pick another straight from
   * the box. We deliberately do NOT re-run the search -- the picked input holds the composed
   * display name, which returns zero matches and would clobber `productResult`. Clear the
   * input's readonly attr imperatively (don't wait for change detection to flush the [readonly]
   * binding) so Material's _canOpen() lets the panel open.
   */
  private reopenCachedResults(): void {
    setTimeout(() => {
      if (!this.productResult?.length) {
        return;
      }
      const el = this.searchStock?.nativeElement;
      if (el) {
        el.readOnly = false;
        el.focus();
      }
      this.autoTrigger?.openPanel();
    }, 0);
  }


  ngAfterViewInit(): void {
    fromEvent(
      this.searchStock.nativeElement, 'keyup'
    ).pipe(
      map((e: any) => {
        return e.target.value || '';
      }),
      // Once a product is picked the input is readonly and holds the (selectable/copyable)
      // name. Suppress the keyup search so pressing e.g. Ctrl to copy doesn't re-open the
      // autocomplete panel, which would clear the user's text selection.
      filter(() => !this.isProductPicked),
      distinctUntilChanged(),
      debounceTime(300),
      switchMap((searchText: string) => {
        this.loading = true;
        searchText = ASCIIFolder.foldReplacing(searchText);
        if (searchText && searchText.length > 2) {
          return this.stockService?.search({ ...this.defaultSearchProduct(searchText) }).pipe(/**gestione error */)
        }

        return new Observable((obs) => obs.next({ messages: [], meta: null, data: [] }));
      })
    ).subscribe((res: any) => {
      this.loading = false;
      const d = res as DynacrudApi<Product[]>;

      this.productResult = d.data || [];

    });
  }

  private defaultSearchProduct(t: string): SearchRequest {

    return {
      filter: {
        filterBy: {
          operation: OperationType.AND,
          filters: [
            { field: 'obsolete', operation: OperationType.EQ, value: false },
          ]
        }, text: t
      }, pagination: { page: 0, size: 20 }, sort: []
    };

  }

  clearSelect() {
    this.searchStock.nativeElement.value = '';
    this.valueProducts[this.code] = undefined;
    this.sharedService.dataProductHeader( {product: this.valueProducts, clear: true} );
  }

  @HostListener('mouseleave') onMouseLeave() {
    this.showImg = false;
    this.hidePreview();
  }

  selectProduct(prod: Product | null) {
    // Picking a result destroys the option (and thumbnail) under the pointer; close the preview
    // here rather than waiting for a boundary event WebKit never sends.
    this.hidePreview();
    if (prod) {
      // The picked name renders through the input's [value]="pickedProductName" binding
      // (readonly input), so it stays selectable/copyable and still re-localizes.
      this.valueProducts[this.code] = prod;
      // add to gestione
    } else {
      this.searchStock.nativeElement.value = '';
      this.valueProducts[this.code] = undefined;
    }

    this.sharedService.dataProductHeader( {product: this.valueProducts, clear: false} );
  }

  /**
   * True once a valid search (>2 chars) has come back with zero products and we're not
   * loading and no product is picked. Drives the "no results" option: Material auto-hides
   * the autocomplete panel when it has no options, so without this the user sees nothing
   * and assumes the search is broken.
   */
  get showNoResults(): boolean {
    const text: string = this.searchStock?.nativeElement?.value ?? '';
    return !this.loading && !this.valueProducts[this.code]?.id && text.trim().length > 2 && this.productResult.length === 0;
  }

  noImage(value: Product): void {
    value.imageName = environment.defaultImageMagic || '';
  }

  /** Opens the enlarged preview for the hovered result, anchored to that row's thumbnail. */
  hoverProduct(e: Event | undefined, product: Product, origin: CdkOverlayOrigin) {
    this.previewProduct = product;
    this.previewOrigin = origin;
    e?.stopPropagation();
  }

  /**
   * Closes the preview. Called from the thumbnail's `mouseleave`, the component-host
   * `mouseleave`, on product pick, and when the autocomplete panel closes -- the last two are
   * what keep it correct on WebKit, which sends no boundary event for a removed node.
   */
  hidePreview(): void {
    this.previewProduct = null;
    this.previewOrigin = null;
  }

}
